import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound, Lock, ShieldCheck } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { authApi } from '@/features/auth/api/auth.api';
import { PasswordStrength } from '@/features/auth/components/PasswordStrength';
import {
  changePasswordSchema,
  type ChangePasswordValues,
} from '@/features/auth/schemas/password.schema';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { applyFieldErrors } from '@/lib/forms';

export function ProfileChangePasswordTab() {
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  const form = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });
  const {
    register,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = form;
  useLiveFormErrors(form);

  const newPassword = watch('newPassword') ?? '';
  const confirmPassword = watch('confirmPassword') ?? '';

  const onChangePassword = handleSubmit(async (values) => {
    setPasswordError(null);
    setPasswordSuccess(null);
    try {
      await authApi.changePassword(values);
      setPasswordSuccess('Your password has been changed successfully. Please keep your new password safe.');
      toast.success('Password changed', 'Your account password has been updated.');
      reset();
      setTimeout(() => {
        setPasswordSuccess(null);
      }, 4000);
    } catch (error) {
      if (error instanceof ApiRequestError) {
        applyFieldErrors(setError, error);
        setPasswordError(error.message);
        return;
      }
      setPasswordError('Could not update password. Please check your current password and try again.');
    }
  });

  return (
    <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 p-5 sm:p-7 shadow-xs flex flex-col gap-6 animate-fade-in">
      {/* Header Section */}
      <div className="flex items-center gap-3.5 border-b border-slate-200/80 dark:border-white/10 pb-4">
        <div className="h-11 w-11 rounded-xl bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-800/60 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0 shadow-2xs">
          <KeyRound className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
            Change Password
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5">
            Update your account password. You will need to use your new password next time you sign in.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Form */}
        <form onSubmit={onChangePassword} className="lg:col-span-7 flex flex-col gap-4">
          {passwordSuccess && (
            <Alert tone="success" title="Password Updated">
              {passwordSuccess}
            </Alert>
          )}

          {passwordError && (
            <Alert tone="critical" title="Update Failed">
              {passwordError}
            </Alert>
          )}

          <Input
            label="Current Password"
            type="password"
            isPassword
            placeholder="Enter your current password"
            error={errors.currentPassword?.message}
            {...register('currentPassword')}
          />

          <Input
            label="New Password"
            type="password"
            isPassword
            placeholder="Enter a new strong password"
            error={errors.newPassword?.message}
            {...register('newPassword')}
          />

          <Input
            label="Confirm New Password"
            type="password"
            isPassword
            placeholder="Re-type your new password"
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />

          <div className="flex items-center gap-3 pt-2">
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting}
              leftIcon={<Lock className="h-4 w-4" />}
            >
              Update Password
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                reset();
                setPasswordError(null);
                setPasswordSuccess(null);
              }}
            >
              Reset
            </Button>
          </div>
        </form>

        {/* Right Column: Password Strength & Requirements */}
        <div className="lg:col-span-5 flex flex-col gap-4">
          <div className="p-4 sm:p-5 rounded-xl bg-slate-50/80 dark:bg-white/5 border border-slate-200/80 dark:border-white/10 flex flex-col gap-3">
            <div className="flex items-center gap-2 text-slate-800 dark:text-slate-200 font-bold text-sm">
              <ShieldCheck className="h-4 w-4 text-indigo-500" />
              <span>Password Requirements</span>
            </div>
            <PasswordStrength value={newPassword} confirmation={confirmPassword} />
          </div>

          <div className="p-4 rounded-xl bg-indigo-50/60 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/30 text-xs text-slate-600 dark:text-slate-300 leading-relaxed flex flex-col gap-1.5">
            <span className="font-bold text-indigo-700 dark:text-indigo-400">Security Tips:</span>
            <ul className="list-disc list-inside space-y-1 text-slate-500 dark:text-slate-400">
              <li>Use at least 8 characters with a mix of letters, numbers, and symbols.</li>
              <li>Never share your student portal password with anyone else.</li>
              <li>Keep your login credentials in a secure place.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
